'use client';

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, Heart, Droplets, Home, Users } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function DixCoupesPage() {
  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-blue-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-blue-500">/</span>
            <span className="text-sm">Marseille</span>
            <span className="text-blue-500">/</span>
            <span className="text-sm">Minör Arkana</span>
            <span className="text-blue-500">/</span>
            <span className="text-sm">Coupes</span>
            <span className="text-blue-500">/</span>
            <span className="text-sm">Dix</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about/tarot/systems/marseille/minor-arcana/coupes"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Coupes'e Geri Dön
          </Link>
        </div>

        {/* Header with Card */}
        <div className="bg-gradient-to-r from-blue-900/30 to-indigo-900/30 rounded-xl p-8 mb-8 border border-blue-600/30">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-1 flex justify-center">
              <div className="w-48 h-72 rounded-xl overflow-hidden shadow-2xl bg-gradient-to-b from-blue-100 to-indigo-50 border border-blue-300 flex flex-col items-center justify-center relative">
                {/* Card Visual Design */}
                <div className="text-center">
                  <div className="text-6xl mb-4">💙</div>
                  <div className="w-16 h-1 bg-blue-600 mx-auto mb-4"></div>
                  <div className="text-2xl font-bold text-blue-800">DIX</div>
                  <div className="text-sm text-blue-700 mt-2">de Coupes</div>
                </div>
                {/* Decorative elements */}
                <div className="absolute top-4 left-4 text-blue-600">
                  <Home className="h-4 w-4" />
                </div>
                <div className="absolute top-4 right-4 text-blue-600">
                  <Home className="h-4 w-4" />
                </div>
                <div className="absolute bottom-4 left-4 text-blue-600">
                  <Users className="h-4 w-4" />
                </div>
                <div className="absolute bottom-4 right-4 text-blue-600">
                  <Users className="h-4 w-4" />
                </div>
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">10</span>
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-slate-100">Dix de Coupes</h1>
                  <p className="text-xl text-blue-400">Aile Mutluluğu / Duygusal Tamamlanma</p>
                </div>
              </div>
              <p className="text-lg text-slate-200 leading-relaxed mb-4">
                On kupanın simgelediği en yüksek duygusal tatmin ve aile huzuru.
                Sevginin tüm formlarının bir araya geldiği, kalplerin birleştiği
                ve mutluluğun paylaşıldığı kutlu anları temsil eder.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-blue-600/20 text-blue-300 rounded-full text-sm">Aile Mutluluğu</span>
                <span className="px-3 py-1 bg-blue-600/20 text-blue-300 rounded-full text-sm">Duygusal Tatmin</span>
                <span className="px-3 py-1 bg-blue-600/20 text-blue-300 rounded-full text-sm">Topluluk</span>
                <span className="px-3 py-1 bg-blue-600/20 text-blue-300 rounded-full text-sm">Huzur</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card Meanings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Positive Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Home className="h-6 w-6 text-green-400" />
              Düz Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Aile Uyumu</h3>
                  <p className="text-slate-400 text-sm">Evde huzur, aile üyeleri arasında sevgi.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Duygusal Bolluk</h3>
                  <p className="text-slate-400 text-sm">Kalp doluluğu, sevgiyle çevrelenme.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Kutlama</h3>
                  <p className="text-slate-400 text-sm">Özel anlar, beraber geçirilen mutlu zamanlar.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">İç Huzur</h3>
                  <p className="text-slate-400 text-sm">Ruhsal tatmin, yaşamdan memnuniyet.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Negative Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Heart className="h-6 w-6 text-red-400" />
              Ters Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Aile Çatışmaları</h3>
                  <p className="text-slate-400 text-sm">Evde huzursuzluk, aile üyeleri arası sorunlar.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Yalnızlık</h3>
                  <p className="text-slate-400 text-sm">Toplumdan izole olma, sevgisizlik hissi.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Sahte Mutluluk</h3>
                  <p className="text-slate-400 text-sm">Görünüşte iyi olan ama içten eksik olan durumlar.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Fazla Beklenti</h3>
                  <p className="text-slate-400 text-sm">Mükemmel aile hayali, gerçekçi olmayan hedefler.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Symbolism */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Marseille Sembolizmi</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-700 rounded-full flex items-center justify-center mx-auto mb-3">
                <Users className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Aile</h3>
              <p className="text-slate-400 text-sm">Birlik, beraberlik ve karşılıklı sevgi.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-indigo-700 rounded-full flex items-center justify-center mx-auto mb-3">
                <Home className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Ev/Yuva</h3>
              <p className="text-slate-400 text-sm">Güvenli liman, sevgi dolu ortam.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-cyan-700 rounded-full flex items-center justify-center mx-auto mb-3">
                <Droplets className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">On Kupa</h3>
              <p className="text-slate-400 text-sm">Maksimum duygusal bereket ve tatmin.</p>
            </div>
          </div>
        </div>

        {/* Life Areas */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yaşam Alanlarındaki Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Home className="h-5 w-5 text-blue-400" />
                Aile ve Ev
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Düğünler ve kutlamalar</li>
                <li>• Çocuk sahibi olma</li>
                <li>• Aile toplantıları</li>
                <li>• Ev içi huzur</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Users className="h-5 w-5 text-indigo-400" />
                İlişkiler
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Uzun vadeli bağlılık</li>
                <li>• Dostluk ve arkadaşlık</li>
                <li>• Toplumsal uyum</li>
                <li>• Karşılıklı destek</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/systems/marseille/minor-arcana/coupes/neuf"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Önceki: Neuf de Coupes
          </Link>

          <Link
            href="/about/tarot/systems/marseille/minor-arcana/coupes/valet"
            className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            Sonraki: Valet de Coupes
            <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}